import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Headphones, Lamp, Shirt, Gamepad2, Tent, Tag } from "lucide-react";
import { CATEGORIES } from "@/data/shop";
import { categoryPath } from "@/lib/seo";
import { useProducts } from "@/lib/products";
import { ProductCard } from "./ProductCard";
import { SectionHead } from "./SectionHead";

const ICONS = { Headphones, Lamp, Shirt, Gamepad2, Tent, Tag };
const BANDS = [
  ["Under £5", "#D9F5E8", "/shop?max=5", (p) => p.price <= 5],
  ["Under £10", "#DCEEFF", "/shop?max=10", (p) => p.price <= 10],
  ["Under £25", "#FFE0EC", "/shop?max=25", (p) => p.price <= 25],
  ["Open Box bargains", "#FFE5D1", "/shop?condition=Open Box", (p) => p.condition === "Open Box"],
];
const bandId = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const live = (products, match) => products.filter((p) => !p.demo && p.qty > 0 && match(p)); // real live stock only

// Always shows something: the asked-for band, or the next band up that has products, else the cheapest band that has any.
const pickBand = (products, start) => [...BANDS.slice(start), ...BANDS].find((b) => live(products, b[3]).length) || BANDS[start];
const short = (t) => t.replace(" bargains", "").replace(/^U/, "u");

const BandResults = ({ band, asked, onAdd, scroll }) => {
  const { products, loading } = useProducts();
  const ref = useRef(null);
  const [title, , , match] = band;
  const items = live(products, match).slice(0, 3);
  useEffect(() => {
    if (!scroll || !window.matchMedia("(max-width: 767px)").matches) return; // only after a tap; on desktop the cards are already in view
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const header = document.querySelector('[data-testid="site-header"]')?.getBoundingClientRect().height || 0; // includes the mobile search row
    const top = ref.current.getBoundingClientRect().top + window.scrollY - header - 12;
    window.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
  }, [title, scroll]);
  return (
    <div ref={ref} className="lg:col-span-8 lg:order-1 scroll-mt-40" data-testid="price-band-results" aria-live="polite">
      <h3 className="font-display text-lg font-bold text-[#202A35] mb-1" data-testid="price-band-results-title">{title}</h3>
      {asked && asked !== title && items.length > 0 && (
        <p className="text-sm text-[#202A35]/70 mb-2" data-testid="price-band-fallback-note">Nothing {short(asked)} right now, so here's {short(title)} instead.</p>
      )}
      {items.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-2" data-testid="price-band-grid">
          {items.map((p, i) => <div key={p.id} className="band-slide-in" style={{ animationDelay: `${i * 70}ms` }}><ProductCard p={p} onAdd={onAdd} /></div>)}
        </div>
      ) : (
        <p className="band-slide-in rounded-2xl bg-[#FFF3C4] p-6 text-sm text-[#202A35]/75" data-testid="price-band-empty">{loading ? "Loading…" : "New bits are added regularly, so check back soon!"}</p>
      )}
    </div>
  );
};

export const Categories = ({ onAdd }) => {
  const { products } = useProducts();
  const [active, setActive] = useState(null); // null = default: the cheapest band that has products
  const band = pickBand(products, Math.max(0, BANDS.findIndex(([t]) => t === active)));
  return (
  <>
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10" data-testid="categories-section">
    <SectionHead eyebrow="Have a nose around" title="Shop by Category" />
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {CATEGORIES.map((c) => {
        const Icon = ICONS[c.icon];
        return (
          <Link key={c.id} to={categoryPath(c.id)} data-testid={`category-card-${c.id}`} style={{ background: c.bg }}
            className="group rounded-2xl p-4 flex items-center gap-3 border-2 border-transparent hover:border-[#202A35] transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:-rotate-1">
            <span className="w-11 h-11 shrink-0 rounded-full bg-white flex items-center justify-center transition-transform duration-300 group-hover:rotate-12" style={{ color: c.fg }}>
              <Icon size={22} strokeWidth={2.2} />
            </span>
            <span>
              <h3 className="font-display font-semibold text-[15px] leading-tight text-[#202A35]">{c.name}</h3>
              <p className="text-xs mt-0.5 text-[#202A35]/65">{c.note}</p>
            </span>
          </Link>
        );
      })}
    </div>
  </section>
  <section className="bg-[#D9F5E8]" data-testid="little-prices-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="lg:col-span-4 lg:order-2 self-center" data-testid="little-prices-strip">
        <span className="inline-block -rotate-2 bg-[#168A5B] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Pocket-money prices</span>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#202A35] tracking-tight">Little Prices, <span className="text-[#168A5B]">Big Finds</span></h2>
        <p className="mt-2 text-sm text-[#202A35]/70">Proper bargains that won't dent your wallet. Pick a price to see what's in.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {BANDS.map(([t]) => (
            <button key={t} type="button" onClick={() => setActive(t)} aria-pressed={band[0] === t} data-testid={`price-band-${bandId(t)}`}
              className={`text-sm font-bold px-4 py-1.5 rounded-full border-2 border-[#202A35] hover:-translate-y-0.5 transition-[transform,background-color,color] ${band[0] === t ? "bg-[#202A35] text-white" : "bg-white text-[#202A35]"}`}>{t}</button>
          ))}
        </div>
      </div>
      <BandResults key={`${active}-${band[0]}`} band={band} asked={active} scroll={!!active} onAdd={onAdd} />
    </div>
  </section>
  </>
  );
};
